import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Input, Select, Spin} from 'antd';
import {SearchOutlined} from '@ant-design/icons';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import Paragraph from 'antd/es/typography/Paragraph';
import {cardActions} from '../../../state/actions/card.actions';
import {cardBackendStatusesConstants, DEFAULT_PAGE_SIZE} from '../../../constants';
import {cardsHelpers} from '../../../utils/helpers';
import {
  StyledSelectDropdownContent,
  StyledSelectDropdownSearchContainer,
} from '../../pages/SubscriptionsPage/tabComponents/SelectDropdown/StyledSelectDropdown';

const SEARCH_DEBOUNCE_MS = 1000;
const SCROLL_LOAD_MORE_THRESHOLD_PX = 20;
const {UNLOCK, LOCK, WAITING, EXPIRED} = cardBackendStatusesConstants;
// Same "still assignable" status set CardsPage.jsx queries with -- a terminated/lost/stolen card
// has nothing to export and would just be dead weight in this picker.
const ACTIVE_CARD_STATUSES = [UNLOCK, LOCK, WAITING, EXPIRED];

// Clone of BudgetRequestSelector.jsx's remote/paginated/search Select, pointed at /cards/ instead
// of /budget-requests/short -- /cards/short (the list already loaded elsewhere on this page) has
// no owner field, so a card's holder name can only come from the full, paginated endpoint.
const CardExportSelector = ({
  allowClear = true,
  className,
  disabled,
  extraOptions = [],
  onChange,
  onResolveLabel,
  placeholder,
  size = 'middle',
  value,
}) => {
  const [t] = useTranslation('main');
  const dispatch = useDispatch();
  const [state, setState] = useState({data: [], loaded: false, loading: false, pagination: null});
  const [searchValue, setSearchValue] = useState('');
  const searchDebounceRef = useRef(null);
  const searchQueryRef = useRef(undefined);
  const loadingRef = useRef(false);

  const loadList = ({query, append = false} = {}) => {
    loadingRef.current = true;
    setState((prev) => ({...prev, loading: true}));
    dispatch(cardActions.getCards(
      {status: ACTIVE_CARD_STATUSES, per_page: DEFAULT_PAGE_SIZE, ...query},
      (response) => {
        loadingRef.current = false;
        const results = (response?.results || []).filter((card) => card.masked_pan);
        setState((prev) => ({
          data: append ? [...prev.data, ...results] : results,
          loaded: true,
          loading: false,
          pagination: response?.pagination || null,
        }));
      },
      () => {
        loadingRef.current = false;
        setState((prev) => ({...prev, loading: false}));
      },
    ));
  };

  // Mirrors BudgetRequestSelector.jsx's `handleOpen` -- `loaded` (not data.length) gates the
  // fetch, so the single-card hydration effect below filling in one row never looks like an
  // already-loaded full page.
  const handleOpen = (open) => {
    if (!open || loadingRef.current || (state.loaded && state.data.length)) return;
    loadList();
  };

  const handleSearchInputChange = (event) => {
    const {value: nextValue} = event.target;
    setSearchValue(nextValue);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchQueryRef.current = nextValue || undefined;
      loadList({query: searchQueryRef.current ? {search: searchQueryRef.current} : undefined});
    }, SEARCH_DEBOUNCE_MS);
  };

  const handlePopupScroll = (event) => {
    const {target} = event;
    const nearBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - SCROLL_LOAD_MORE_THRESHOLD_PX;
    if (nearBottom && !loadingRef.current && state.pagination?.cursor) {
      loadList({
        query: {cursor: state.pagination.cursor, ...(searchQueryRef.current ? {search: searchQueryRef.current} : {})},
        append: true,
      });
    }
  };

  useEffect(() => () => clearTimeout(searchDebounceRef.current), []);

  useEffect(() => {
    const isExtraOption = extraOptions.some((option) => option.value === value);
    if (!value || isExtraOption || state.data.some((card) => card.card_id === value)) return;
    dispatch(cardActions.getCard(
      value,
      (card) => setState((prev) => (
        prev.data.some((item) => item.card_id === value) ? prev : {...prev, data: [card, ...prev.data]}
      )),
    ));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = useMemo(() => [
    ...extraOptions,
    // A card the caller already resolved (ExportWizard seeds its own picked card into
    // `extraOptions` so a remount -- going back a step -- doesn't flash the raw card id while this
    // component re-fetches it) would otherwise also arrive here once the full list loads, as a
    // second, identical row.
    ...state.data
      .filter((card) => !extraOptions.some((option) => option.value === card.card_id))
      .map((card) => ({
        value: card.card_id,
        label: `${card.owner?.full_name || card.name} •• ${cardsHelpers.hideCardNumber(card.masked_pan, 4)}`,
      })),
  ], [extraOptions, state.data]);

  // Single place the caller learns the current value's display label -- fires for the default
  // ("All cards"/an extra option, resolvable immediately) just as much as for a hydrated single
  // card (once the value-hydration effect above lands it in `options`) or a plain user pick.
  useEffect(() => {
    if (!onResolveLabel) return;
    const match = options.find((option) => option.value === value);
    if (match) onResolveLabel(value, match.label);
  }, [value, options]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasNoResults = !state.loading && options.length === 0;

  return (
    <Select
      allowClear={allowClear}
      className={className}
      disabled={disabled}
      // See BudgetRequestSelector.jsx's identical comment: an empty options list with a falsy
      // notFoundContent keeps rc-select from ever opening the popup.
      notFoundContent={<span />}
      onChange={onChange}
      onOpenChange={handleOpen}
      onPopupScroll={handlePopupScroll}
      options={options}
      placeholder={placeholder}
      popupRender={(menu) => (
        <StyledSelectDropdownContent>
          <StyledSelectDropdownSearchContainer className='search'>
            <Input
              allowClear
              onChange={handleSearchInputChange}
              placeholder={t('search')}
              size='middle'
              suffix={<SearchOutlined />}
              value={searchValue}
            />
          </StyledSelectDropdownSearchContainer>
          <div className='dropdown-searched-content'>
            {menu}
            {state.loading && (
              <div style={{padding: 12, textAlign: 'center'}}>
                <Spin size='small' />
              </div>
            )}
            {hasNoResults && (
              <Paragraph>{t('noResultsFound')}</Paragraph>
            )}
          </div>
        </StyledSelectDropdownContent>
      )}
      showSearch={false}
      size={size}
      value={value}
    />
  );
};

CardExportSelector.propTypes = {
  allowClear: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  extraOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
  })),
  onChange: PropTypes.func,
  onResolveLabel: PropTypes.func,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  value: PropTypes.string,
};

export default CardExportSelector;
